import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { toast } from "sonner";
import { useLanguage } from "../../i18n/language-provider";
import type { AppRoute } from "../../navigation/routes";
import {
    forgetAssistantConnection,
    selectAssistantConnection,
    useAssistantChat,
} from "../../services/assistantChat";
import { fetchStrategyHelpStatus, type StrategyHelpStatus } from "../../services/strategyHelpApi";
import { RenderFaultBoundary } from "../RenderFaultBoundary";
import { StrategyHelpRequestDialog, type AskOptivoWindowView } from "./StrategyHelpRequestDialog";
import { assistantChatCopy } from "./assistant-chat-copy";
import { strategyHelpCopy } from "./strategy-help-copy";
import { SUPPORT_EMAIL } from "./support-contact";

/** One notice for a window that could not be shown, however often it is asked for. */
const WINDOW_FAILED_TOAST_ID = "ask-optivo-window-failed";

/** Long enough to read an address and write it down. */
const WINDOW_FAILED_TOAST_MS = 12_000;

/** One notice for an answer that arrived with no entry on the screen to say so. */
const ASSISTANT_ANSWERED_TOAST_ID = "ask-optivo-assistant-answered";

interface AskOptivoContextValue {
    /** What is waiting with Optivo, as last read; null until read, or when it could not be. */
    status: StrategyHelpStatus | null;
    /** Where the one request window is: open, minimised into the rail, or put away. */
    view: AskOptivoWindowView;
    /** Open the window, bring it back from the rail, or bring it forward while it is open. */
    ask: (from?: HTMLElement | null) => void;
    /** Ticks each time the window is minimised, for the entry it went into to flash. */
    minimizedCount: number;
    /** An "Ask Optivo" entry on the screen, for the focus to come back to; returns its removal. */
    registerEntry: (element: HTMLElement) => () => void;
    /** Whether the window offers the live assistant. */
    assistantAvailable: boolean;
    /** The assistant answered while the window was not open, and nobody has looked yet. */
    answered: boolean;
    /** Ticks each time such an answer arrives, for the entry to flash. */
    answeredCount: number;
}

const AskOptivoContext = createContext<AskOptivoContextValue | null>(null);

/** The request window and its status, for an "Ask Optivo" entry. */
export function useAskOptivo(): AskOptivoContextValue {
    const value = useContext(AskOptivoContext);
    if (!value) throw new Error("useAskOptivo is used outside an AskOptivoProvider");
    return value;
}

/**
 * Whether an entry is on the screen without moving the focus to find out: an
 * answer arriving must not take the focus from whatever the operator is
 * doing. Where the browser cannot say, an entry in the page counts.
 */
function anEntryIsOnScreen(entries: Iterable<HTMLElement>): boolean {
    for (const entry of entries) {
        if (!entry.isConnected) continue;
        if (typeof entry.checkVisibility !== "function" || entry.checkVisibility()) return true;
    }
    return false;
}

/**
 * The focus goes back to an "Ask Optivo" entry on the screen — the one that
 * opened the window if it is still there. An entry that cannot take it (the
 * desktop rail is drawn but not shown on a phone) is passed over, so whether
 * the focus landed is whether an entry is actually on the screen.
 */
function focusAnEntry(opener: HTMLElement | null, entries: Iterable<HTMLElement>): boolean {
    const candidates = [opener, ...entries].filter(
        (element): element is HTMLElement => element != null && element.isConnected,
    );
    for (const candidate of candidates) {
        candidate.focus({ preventScroll: true });
        if (document.activeElement === candidate) return true;
    }
    return false;
}

interface AskOptivoProviderProps {
    /**
     * Whether asking is possible: a property open, the one the tab was loaded
     * for, and not only read. While it is not, nothing is read and the window
     * is taken off the screen — not put away, so it comes back as it was.
     */
    enabled: boolean;
    /** Changes whenever the status may have: the open property, or the page on screen. */
    refreshKey: string;
    /**
     * The property the window is for. Another one stops an answer streaming
     * for the last and drops its conversation; none leaves the assistant
     * alone (a harness that names no property).
     */
    connectionId?: number | null;
    /** Leaves for a screen an assistant's answer links to; the shell decides whether it is open. */
    onNavigate?: (route: AppRoute) => void;
    /** The shell's verdict on a screen, so a link it would refuse reads as text. */
    isPageAllowed?: (route: AppRoute) => boolean | "pending";
    children: ReactNode;
}

/**
 * The one "Ask Optivo" window, and what is waiting with Optivo, for every
 * entry that opens it.
 *
 * Mounted once by the rail's owner (`Sidebar`) around both of its renders —
 * the desktop rail and the phone's menu — because the window outlives every
 * one of them: the phone's menu folds away the moment the window opens, the
 * rail folds and unfolds under it, and the page beside it changes, while the
 * question being written must survive all three. The entries only ask
 * (`useAskOptivo`); the window, its draft and the status read for them live
 * here, read once however many entries are drawn.
 *
 * When the window leaves the screen the focus goes back to an entry, and one
 * it was minimised into flashes, so the operator sees where it went. With no
 * entry on the screen — a phone, its menu folded away — a toast says where to
 * find it instead.
 *
 * **A window that cannot be drawn costs the window, not the rail.** It is
 * drawn inside a boundary of its own, being mounted here, above both renders
 * of the rail: a render fault in it used to take the navigation along, and
 * before that the whole app. Nothing can stand in its place, a window being
 * no fixed part of the screen, so the fault is said in a notice instead: that
 * the window could not be shown, to reload, and the address to write to, a
 * request not being sendable from a window that is not there. The window is
 * put away with the fault, so no entry goes on saying one is open or
 * minimised behind it, and what was written in it is lost with it. It is
 * tried again each time it is asked for, and said again while it still cannot
 * be drawn. A fault while nobody had the window open or minimised says
 * nothing, since nothing the operator asked for failed.
 *
 * **The live assistant's conversation is not the window's.** It lives in a
 * module store (`services/assistantChat.ts`) that outlives the window, its
 * boundary and this provider, so an answer streams in while the window is
 * minimised, closed or lost to a fault. What this provider adds is the
 * property it is for (`connectionId`: another one stops the answer and drops
 * the conversation, never a minimise or a page change) and the word on the
 * rail: an answer that ends while the window is not open makes the entry
 * flash and say the assistant answered, until the window is opened or the
 * conversation changes — and, with no entry on the screen, says it once in a
 * toast. The assistant is offered only where the status says so
 * (`assistantAvailable`) and for a property.
 */
export function AskOptivoProvider({ enabled, refreshKey, connectionId, onNavigate, isPageAllowed, children }: AskOptivoProviderProps) {
    const { t } = useLanguage();
    const copy = strategyHelpCopy(t);
    const answeredNotice = assistantChatCopy(t).answered;
    const chat = useAssistantChat();
    const [answered, setAnswered] = useState(false);
    const [answeredCount, setAnsweredCount] = useState(0);
    const seenAnswer = useRef(chat.answerSignal);
    const [status, setStatus] = useState<StrategyHelpStatus | null>(null);
    const [view, setView] = useState<AskOptivoWindowView>("closed");
    const [focusRequest, setFocusRequest] = useState(0);
    const [minimizedCount, setMinimizedCount] = useState(0);
    // Ticks each time the window is asked for: what a window that could not be
    // drawn is tried again on.
    const [asked, setAsked] = useState(0);
    // The view as of the last commit, for `ask` to read between renders.
    const viewNow = useRef<AskOptivoWindowView>("closed");
    const opener = useRef<HTMLElement | null>(null);
    const entries = useRef(new Set<HTMLElement>());
    const elsewhereNotice = copy.windowMinimizedElsewhere;

    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        fetchStrategyHelpStatus()
            .then((next) => {
                if (alive) setStatus(next);
            })
            .catch(() => {
                // The entries still open the window; only the status line is lost.
            });
        return () => {
            alive = false;
        };
    }, [enabled, refreshKey]);

    useEffect(() => {
        const previous = viewNow.current;
        viewNow.current = view;
        if (previous !== "open" || view === "open") return;
        const landed = focusAnEntry(opener.current, entries.current);
        if (view !== "minimized") return;
        setMinimizedCount((count) => count + 1);
        if (!landed) toast.info(elsewhereNotice);
    }, [view, elsewhereNotice]);

    // The conversation is the open property's: another one stops an answer
    // streaming for the last and drops what was being written for it.
    useEffect(() => {
        if (connectionId === undefined) return;
        if (connectionId === null) forgetAssistantConnection();
        else selectAssistantConnection(connectionId);
    }, [connectionId]);

    // An answer that ended while the window was minimised or closed is said
    // on the entry it went into, and in a toast where none is on the screen.
    useEffect(() => {
        if (chat.answerSignal === seenAnswer.current) return;
        seenAnswer.current = chat.answerSignal;
        if (viewNow.current === "open") return;
        setAnswered(true);
        setAnsweredCount((count) => count + 1);
        if (!anEntryIsOnScreen(entries.current)) toast.info(answeredNotice, { id: ASSISTANT_ANSWERED_TOAST_ID });
    }, [chat.answerSignal, answeredNotice]);

    // Looked at, or no longer the conversation it was about.
    useEffect(() => {
        if (view === "open") setAnswered(false);
    }, [view]);
    useEffect(() => {
        setAnswered(false);
    }, [chat.conversationId]);

    const ask = useCallback((from?: HTMLElement | null) => {
        if (from) opener.current = from;
        setAsked((count) => count + 1);
        if (viewNow.current === "open") setFocusRequest((count) => count + 1);
        else setView("open");
    }, []);

    // Told by the window's boundary of every fault in it. Read at the catch,
    // so `view` is the one the window was being drawn for.
    const handleWindowFault = () => {
        if (view === "closed") return;
        setView("closed");
        toast.error(copy.windowFailed, {
            id: WINDOW_FAILED_TOAST_ID,
            description: copy.windowFailedHint.replace("{email}", SUPPORT_EMAIL),
            duration: WINDOW_FAILED_TOAST_MS,
        });
    };

    const registerEntry = useCallback((element: HTMLElement) => {
        entries.current.add(element);
        return () => {
            entries.current.delete(element);
        };
    }, []);

    const onSent = useCallback((request: NonNullable<StrategyHelpStatus["openRequest"]>) => {
        setStatus((previous) => ({
            ...previous,
            contactEmail: previous?.contactEmail ?? null,
            colleagueEmails: previous?.colleagueEmails ?? [],
            pendingProposal: previous?.pendingProposal ?? null,
            openSetupRequest: previous?.openSetupRequest ?? null,
            openRequest: request,
        }));
    }, []);

    // Offered only for a property, which the conversation is about.
    const assistantAvailable = status?.assistantAvailable === true && connectionId != null;

    const value = useMemo<AskOptivoContextValue>(
        () => ({ status, view, ask, minimizedCount, registerEntry, assistantAvailable, answered, answeredCount }),
        [status, view, ask, minimizedCount, registerEntry, assistantAvailable, answered, answeredCount],
    );

    return (
        <AskOptivoContext.Provider value={value}>
            {children}
            <RenderFaultBoundary
                part="askOptivo.window"
                retryKey={asked}
                fallback={null}
                onFault={handleWindowFault}
            >
                <StrategyHelpRequestDialog
                    view={view}
                    onViewChange={setView}
                    hidden={!enabled}
                    focusRequest={focusRequest}
                    contactEmail={status?.contactEmail ?? null}
                    colleagueEmails={status?.colleagueEmails}
                    onSent={onSent}
                    assistantAvailable={assistantAvailable}
                    connectionId={connectionId ?? null}
                    onNavigate={onNavigate}
                    isPageAllowed={isPageAllowed}
                />
            </RenderFaultBoundary>
        </AskOptivoContext.Provider>
    );
}
